import { Dispatch, FC, SetStateAction, useEffect, useCallback, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import Swal from 'sweetalert2';
import moment from 'moment';

import {
    Switch,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Box,
    Typography,
    Divider,
    Tooltip,
    Checkbox,
    ListItemText,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { SCUIGeneralModal } from '../../../../../../../../atomic/molecules';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { RTM } from './ruleTabModal.classes';

import { RuleServiceCommand } from '../../../../../../../../services/command';
import { useRuleService } from '../../../../../../context/RuleServiceContext';
import { getOperators, RuleTenant } from '../../../../../../helper/RuleServiceV2.const';
import { getCountryCode, validateRule } from '../../../../../../helper/RuleServiceV2.helper';
import { formatSelectedCriteriaNames, toggleCriteriaSelectedValue } from './ruleTabModal.helpers';

type IProps = {
    isModalOpen: boolean;
    setIsModalOpen: Dispatch<SetStateAction<boolean>>;
    selectedRule: any;
    setSelectedRule: Dispatch<SetStateAction<any>>;
};

type IFormData = {
    id: string | number;
    ruleKey: string;
    ruleTemplateId: string;
    priority: string | number;
    fromDate: string | Date;
    endDate: string | Date;
    action: string;
    intervalValues: any[];
    criteria: any;
    selectedAllIndex: any[];
    regionId: any;
};

const defaultValues = {
    id: '',
    ruleKey: '',
    ruleTemplateId: '',
    priority: '',
    fromDate: '',
    endDate: '',
    action: '',
    intervalValues: [],
    criteria: [],
    selectedAllIndex: [],
    regionId: JSON.parse(localStorage.getItem('selectedCountry') || '{}')?.regionId,
};
const countryId: any = JSON.parse(localStorage.getItem('selectedCountry') || '{}')?.countryId?.toString();

export const RuleTabModal: FC<IProps> = ({ setSelectedRule, selectedRule, isModalOpen, setIsModalOpen }) => {
    const [ruleTemplates, setRuleTemplates] = useState<any[]>([]);
    const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
    const [jsonInput, setJsonInput] = useState('');

    const { tenant, setIsLoading, fetchRuleGridData, displayGlobalRules } = useRuleService();
    const { t } = useTranslation();
    const { register, reset, watch, setValue, getValues } = useForm<IFormData>({
        defaultValues,
    });
    const ruleTemplateId = watch('ruleTemplateId');
    const criteria = watch('criteria');
    const intervalValues = watch('intervalValues');
    const selectedAllIndex = watch('selectedAllIndex');

    const operators = useMemo(() => getOperators(), []);

    const criteriaTypes = useMemo(() => {
        if (ruleTemplateId && ruleTemplates) {
            return ruleTemplates.find((item: any) => item.id === ruleTemplateId)?.criteriaTypes;
        }
        return [];
    }, [ruleTemplateId, ruleTemplates]);

    const handleModalClose = () => {
        reset(defaultValues);
        setSelectedRule(undefined);
        setIsModalOpen(false);
    };

    const updateChildItem = (value: any, criteriaTypeKey: any, operator: number, index: number) => {
        let tempCriteria = [...(criteria || [])];
        tempCriteria[index] = {
            ...tempCriteria[index],
            key: criteriaTypeKey,
            operator: operator,
            value: value,
        };
        setValue('criteria', tempCriteria);
    };

    const updateOperatorValue = (value: any, criteriaTypeKey: any, operator: any, index: number, key: keyof any) => {
        let operatorValue = [...(intervalValues || [])];
        operatorValue[index] = {
            ...operatorValue[index],
            [key]: value,
        };

        setValue('intervalValues', operatorValue);

        let criteriaValue = `${operatorValue[index]?.ValueA ?? ''}#${operatorValue[index]?.ValueB ?? ''}`;

        updateChildItem(criteriaValue, criteriaTypeKey, operator, index);
    };

    const getAllRuleTemplates = useCallback(async () => {
        const response = await RuleServiceCommand.GetRuleTemplateList({
            Tenant: tenant,
            Skip: 0,
        });

        if (response?.data) {
            setRuleTemplates(response.data);
        }
    }, [tenant, setRuleTemplates]);

    const setSelectedAll = (val: boolean, index: number) => {
        let items = [...selectedAllIndex];
        items[index] = { disabled: val };
        setValue('selectedAllIndex', items);
    };

    const handleSubmit = async () => {
        setIsLoading(true);

        let payload: any = {
            regionId: getValues('regionId'),
            id: getValues('id'),
            ruleTemplateId: getValues('ruleTemplateId'),
            ruleKey: getValues('ruleKey'),
            action: getValues('action'),
            criteria: getValues('criteria'),
            Tenant: tenant,
            countryId: displayGlobalRules ? 0 : countryId,
        };

        payload.criteria = payload.criteria?.map((item: any) => {
            return {
                key: item.key,
                operator: item.operator,
                value: item.value,
                rules: item.rules,
            };
        });

        let validation = validateRule(payload);

        if (!validation.isValid) {
            Swal.fire(t('general.error'), t('general.emptyFieldControlText'), 'error');
            setIsLoading(false);
            return;
        }
        RuleServiceCommand.CreateRule({
            rules: [
                {
                    ...payload,
                },
            ],
        })
            .then(() => {
                Swal.fire(t('general.success'), '', 'success');
                setSelectedRule(undefined);
                fetchRuleGridData();
            })
            .catch(() => {
                Swal.fire(t('general.error'), '', 'error');
                setIsLoading(false);
            })
            .finally(() => {
                setIsLoading(false);
                handleModalClose();
            });
    };

    const handleModalConfirm = () => {
        handleSubmit();
    };

    const handleBlockChars = (event: any) => {
        const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'];
        if (!/[0-9]/.test(event.key) && !allowedKeys.includes(event.key)) {
            event.preventDefault();
        }
    };

    const handleJsonDialogOpen = () => setJsonDialogOpen(true);
    const handleJsonDialogClose = () => setJsonDialogOpen(false);

    const handleJsonSubmit = () => {
        try {
            const parsedData = JSON.parse(jsonInput);
            reset(parsedData);
            handleJsonDialogClose();
        } catch {
            Swal.fire(t('ruleManagement.invalidJsonError'), '', 'error');
        }
    };

    const generateJson = () => {
        const formData = getValues();
        const jsonString = JSON.stringify(formData, null, 2);
        navigator.clipboard
            .writeText(jsonString)
            .then(() => {
                Swal.fire({
                    title: t('ruleManagement.exportJsonTitle'),
                    text: t('ruleManagement.exportJsonSuccess'),
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false,
                });
            })
            .catch(() => {
                Swal.fire({
                    title: t('ruleManagement.exportJsonTitle'),
                    text: t('ruleManagement.exportJsonError'),
                    icon: 'error',
                });
            });
    };

    useEffect(() => {
        if (criteriaTypes && !selectedRule?.id) {
            setValue('criteria', criteriaTypes);
            return;
        }

        if (selectedRule?.ruleTemplateId === ruleTemplateId) {
            let tempIntervalValues: any[] = [];
            let tempSelectedAllIndex: any[] = [];

            selectedRule?.criteria?.forEach((item: any) => {
                const tempIndex = (criteriaTypes || []).indexOf(
                    (criteriaTypes || []).find((type: any) => type.key === item.key)
                );
                tempSelectedAllIndex[tempIndex] = {
                    disabled: criteriaTypes?.[tempIndex]?.criteriaTypeValues?.every((typeValue: any) =>
                        item.value?.split(',').includes(typeValue.value)
                    ),
                };
                if (
                    [
                        operators?.[1].value,
                        operators?.[2].value,
                        operators?.[3].value,
                        operators?.[4].value,
                        operators?.[6].value,
                        operators?.[7].value,
                        operators?.[8].value,
                        operators?.[9].value,
                    ].includes(item.operator)
                ) {
                    let values = item.value?.split('#');
                    tempIntervalValues[tempIndex] = { ValueA: values[0], ValueB: values[1] };
                }
            });

            setValue('intervalValues', tempIntervalValues);
            setValue('selectedAllIndex', tempSelectedAllIndex);
        }

        if (criteriaTypes?.length > 0) {
            const tempCriterias = criteriaTypes.map((item: any) => {
                const sameKeyCriteria = (criteria || []).find((type: any) => type.key === item.key);
                if (sameKeyCriteria) {
                    return {
                        ...item,
                        value: sameKeyCriteria.value,
                    };
                }
                return null;
            });
            if (tempCriterias?.some((item: any) => item === null)) {
                setValue('criteria', criteriaTypes);
                return;
            }
            setValue('criteria', tempCriterias);
            return;
        }
        setValue('criteria', []);
    }, [criteriaTypes, setValue]);

    useEffect(() => {
        getAllRuleTemplates();
    }, [getAllRuleTemplates]);

    useEffect(() => {
        if (selectedRule && selectedRule.id) {
            reset({
                id: selectedRule.id,
                action: selectedRule.action,
                priority: selectedRule.priority,
                ruleTemplateId: selectedRule.ruleTemplateId,
                ruleKey: selectedRule.ruleKey,
                criteria: selectedRule.criteria,
                regionId: selectedRule.regionId,
                fromDate: moment(selectedRule.effectiveDate).format('DD/MM/YYYY'),
                endDate: moment(selectedRule.endDate).format('DD/MM/YYYY'),
            });
        }
    }, [selectedRule, operators, reset]);

    return (
        <SCUIGeneralModal
            width={'md'}
            open={isModalOpen}
            onClose={handleModalClose}
            title={
                <Box className={RTM.titleLayout} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <Typography className={RTM.titleText} variant="h6">
                        {t('ruleManagement.modal.rule.title')}
                    </Typography>
                    <Box className={RTM.titleActions}>
                        <Tooltip title={t('ruleManagement.importJsonTitle')}>
                            <IconButton className={RTM.importJsonButton} onClick={handleJsonDialogOpen} color="primary">
                                <FileUploadIcon className={RTM.importJsonIcon} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={t('ruleManagement.exportJsonTitle')}>
                            <IconButton className={RTM.exportJsonButton} onClick={generateJson} color="secondary">
                                <FileDownloadIcon className={RTM.exportJsonIcon} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            }
            functionButtonOnClick={handleModalConfirm}
            buttonText={t('general.save')}
            rootClassName={RTM.modalRoot}
            paperClassName={RTM.modalPaper}
            titleClassName={RTM.modalTitle}
            closeIconButtonClassName={RTM.modalCloseButton}
            closeIconClassName={RTM.modalCloseIcon}
            sx={{
                '.MuiPaper-root': {
                    padding: 2,
                    maxHeight: '90vh',
                    overflowY: 'auto',
                },
            }}
        >
            <form className={RTM.form}>
                <Grid
                    className={RTM.containerGrid}
                    container
                    sx={{
                        padding: '2rem',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '8px',
                        minHeight: '450px',
                        height: 'auto',
                        alignItems: 'stretch',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                    }}
                >
                    <Box
                        className={RTM.leftPanel}
                        sx={{ display: 'flex', width: '100%', flexDirection: 'column', flex: 1, minHeight: '400px' }}
                    >
                        <Box
                            className={RTM.metaCard}
                            sx={{
                                padding: '16px',
                                backgroundColor: '#fff',
                                borderRadius: '8px',
                                boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                                marginBottom: '12px',
                            }}
                        >
                            <Typography variant="subtitle1" gutterBottom>
                                {t('ruleManagement.fields.ruleTemplate.tenant')}:{' '}
                                {RuleTenant[+tenant as keyof typeof RuleTenant] || ''}
                            </Typography>
                            <Typography variant="subtitle1" gutterBottom sx={{ marginBottom: '0' }}>
                                {t('general.region')}: {getCountryCode() || ''}
                            </Typography>
                        </Box>
                        <FormControl className={RTM.templateSelectControl} fullWidth sx={{ margin: '12px 0' }}>
                            <InputLabel className={RTM.templateSelectLabel} id={'ruleTemplateId'}>
                                {t('ruleManagement.fields.rule.ruleTemplate.name')}
                            </InputLabel>
                            <Select
                                className={RTM.templateSelect}
                                sx={{ boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }}
                                labelId={'ruleTemplateId'}
                                id={'field.ruleTemplateId'}
                                size="medium"
                                {...register('ruleTemplateId')}
                                value={ruleTemplateId || ''}
                                onChange={(e: any) => {
                                    setValue('intervalValues', []);
                                    setValue('selectedAllIndex', []);
                                    setValue('ruleTemplateId', e.target.value);
                                    setValue('criteria', []);
                                }}
                                label={t('ruleManagement.fields.rule.ruleTemplate.name')}
                            >
                                {(ruleTemplates || []).map((ruleTemplate: any, index: number) => (
                                    <MenuItem
                                        className={RTM.templateSelectItem}
                                        key={index}
                                        id={ruleTemplate.id}
                                        value={ruleTemplate.id}
                                        data-rule-template-id={ruleTemplate.id}
                                    >
                                        {ruleTemplate.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth sx={{ margin: '12px 0' }}>
                            <TextField
                                className={RTM.ruleKeyField}
                                sx={{ boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }}
                                required
                                {...register('ruleKey')}
                                label={t('ruleManagement.fields.rule.ruleKey.name')}
                                value={watch('ruleKey') || ''}
                                fullWidth
                            />
                        </FormControl>
                        <FormControl fullWidth sx={{ marginTop: '12px' }}>
                            <TextField
                                className={RTM.actionField}
                                required
                                sx={{ boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }}
                                {...register('action')}
                                value={watch('action') || ''}
                                label={t('ruleManagement.fields.rule.action.name')}
                                fullWidth
                                multiline
                                minRows={3}
                            />
                        </FormControl>
                    </Box>
                    <Divider className={RTM.divider} orientation="vertical" flexItem sx={{ mx: '16px', backgroundColor: '#ccc' }} />
                    <Box
                        className={RTM.criteriaPanel}
                        sx={{
                            flex: 2,
                            padding: '16px',
                            minHeight: '400px',
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                            overflow: 'auto',
                        }}
                    >
                        {criteria?.length > 0 ? (
                            (criteria || []).map((item: any, index: number) => (
                                <Box
                                    key={index}
                                    className={RTM.criteriaCard}
                                    data-criteria-index={index}
                                    sx={{
                                        padding: '16px',
                                        border: '1px solid rgba(0, 0, 0, 0.2)',
                                        borderRadius: '8px',
                                        marginBottom: '16px',
                                        backgroundColor: '#fafafa',
                                    }}
                                >
                                    <div style={{ flexDirection: 'row' }}>
                                        <TextField
                                            className={RTM.criteriaNameField}
                                            required
                                            sx={{ margin: '12px 0' }}
                                            value={item.name || ''}
                                            label={t('ruleManagement.fields.ruleTemplate.criteriaType.name')}
                                            disabled
                                            fullWidth
                                        />
                                    </div>
                                    <FormControl
                                        className={RTM.operatorControl}
                                        fullWidth
                                        sx={{ margin: '12px 0', display: 'flex', flexDirection: 'row' }}
                                    >
                                        <InputLabel className={RTM.operatorLabel} id={'Operator'}>
                                            {operators?.[item.operator]?.label}
                                        </InputLabel>
                                        <Select
                                            className={RTM.operatorSelect}
                                            labelId={'Operator'}
                                            disabled
                                            fullWidth
                                            id={'Operator'}
                                            size="medium"
                                            label={'Operator'}
                                            value={operators?.[item.operator]?.value || ''}
                                        >
                                            <MenuItem
                                                className={RTM.operatorSelectItem}
                                                id={'Operator'}
                                                value={operators?.[item.operator]?.value}
                                            >
                                                {operators?.[item.operator]?.label}
                                            </MenuItem>
                                        </Select>
                                        <Tooltip
                                            title={
                                                <>
                                                    İşlem bölümünde yer alan X değeri parametreyi temsil etmektedir.
                                                    İçeren işlemi seçildiğinde, parametrenizin değer kısmına yazacağınız
                                                    veriyi içereceği anlamına gelir. Diğer işlemler için girilecek olan
                                                    A ve B değerleri parametrenizin hangi aralıkta olacağını
                                                    belirleyecektir.
                                                    <br />
                                                    <strong>Örnek:</strong> Kural tanımlanırken, 10&lt;X&lt;15
                                                    girildiğinde tanımlamış olduğunuz X parametresi 10&apos;dan büyük,
                                                    15&apos;ten küçük olacak şekilde kural işleyecektir.
                                                </>
                                            }
                                        >
                                            <IconButton
                                                className={RTM.operatorHelpButton}
                                                size="large"
                                                sx={{
                                                    width: '40px',
                                                    padding: '0px',
                                                    marginLeft: '18px',
                                                }}
                                            >
                                                <HelpOutlineOutlinedIcon
                                                    className={RTM.operatorHelpIcon}
                                                    sx={{
                                                        width: '25px !important',
                                                        height: '25px !important',
                                                    }}
                                                />
                                            </IconButton>
                                        </Tooltip>
                                    </FormControl>
                                    <Box style={{ width: '100%' }}>
                                        {!item.hasPredefinedValue && (item.operator === 0 || item.operator === 5 || item.operator === 10 || item.operator === 11) && (
                                            <FormControl fullWidth>
                                                <TextField
                                                    className={RTM.valueSingleField}
                                                    required
                                                    value={criteria?.[index]?.value || ''}
                                                    onChange={(event: any) => {
                                                        updateChildItem(
                                                            event.target.value,
                                                            item.key,
                                                            operators?.[item.operator]?.value,
                                                            index
                                                        );
                                                    }}
                                                    label={t('ruleManagement.fields.rule.values.value')}
                                                    fullWidth
                                                />
                                            </FormControl>
                                        )}
                                        {!item.hasPredefinedValue &&
                                            [
                                                operators?.[1].value,
                                                operators?.[2].value,
                                                operators?.[3].value,
                                                operators?.[4].value,
                                            ].includes(item.operator) && (
                                                <FormControl
                                                    sx={{ display: 'flex', 'flex-direction': 'row' }}
                                                    fullWidth
                                                >
                                                    <TextField
                                                        className={RTM.valueAField}
                                                        required
                                                        value={intervalValues?.[index]?.ValueA || ''}
                                                        onKeyDown={(event) => {
                                                            handleBlockChars(event);
                                                        }}
                                                        onChange={(event: any) => {
                                                            updateOperatorValue(
                                                                event.target.value,
                                                                item.key,
                                                                operators?.[item.operator]?.value,
                                                                index,
                                                                'ValueA'
                                                            );
                                                        }}
                                                        label={t('ruleManagement.fields.rule.values.valueA')}
                                                        sx={{ 'margin-right': '5px' }}
                                                        fullWidth
                                                    />
                                                    <TextField
                                                        className={RTM.valueBField}
                                                        required
                                                        onKeyDown={(event) => {
                                                            handleBlockChars(event);
                                                        }}
                                                        value={intervalValues?.[index]?.ValueB || ''}
                                                        onChange={(event: any) => {
                                                            updateOperatorValue(
                                                                event.target.value,
                                                                item.key,
                                                                operators?.[item.operator]?.value,
                                                                index,
                                                                'ValueB'
                                                            );
                                                        }}
                                                        label={t('ruleManagement.fields.rule.values.valueB')}
                                                        fullWidth
                                                    />
                                                </FormControl>
                                            )}
                                        {!item.hasPredefinedValue &&
                                            [
                                                operators?.[6].value,
                                                operators?.[7].value,
                                                operators?.[8].value,
                                                operators?.[9].value,
                                            ].includes(item.operator) && (
                                                <FormControl
                                                    sx={{ display: 'flex', 'flex-direction': 'row' }}
                                                    fullWidth
                                                >
                                                    <TextField
                                                        className={RTM.valueAField}
                                                        value={intervalValues?.[index]?.ValueA || ''}
                                                        onChange={(event: any) => {
                                                            updateOperatorValue(
                                                                event.target.value,
                                                                item.key,
                                                                operators?.[item.operator]?.value,
                                                                index,
                                                                'ValueA'
                                                            );
                                                        }}
                                                        label={t('ruleManagement.fields.rule.values.valueA')}
                                                        sx={{ 'margin-right': '5px' }}
                                                        fullWidth
                                                    />
                                                    <TextField
                                                        className={RTM.valueBField}
                                                        value={intervalValues?.[index]?.ValueB || ''}
                                                        onChange={(event: any) => {
                                                            updateOperatorValue(
                                                                event.target.value,
                                                                item.key,
                                                                operators?.[item.operator]?.value,
                                                                index,
                                                                'ValueB'
                                                            );
                                                        }}
                                                        label={t('ruleManagement.fields.rule.values.valueB')}
                                                        fullWidth
                                                    />
                                                </FormControl>
                                            )}
                                        {item.hasPredefinedValue && item.criteriaTypeValues && (
                                            <FormControl
                                                sx={{
                                                    'flex-direction': 'row',
                                                    'justify-content': 'space-between',
                                                }}
                                                fullWidth
                                            >
                                                <InputLabel id={'criteriaTypeValue.id'}>{item.name}</InputLabel>
                                                <Select
                                                    className={RTM.predefinedSelect}
                                                    sx={{ flex: 6 }}
                                                    multiple
                                                    labelId={t('ruleManagement.fields.rule.values.value')}
                                                    id={'criteriaTypeValue.id'}
                                                    disabled={selectedAllIndex?.[index]?.disabled}
                                                    size="medium"
                                                    value={
                                                        criteria?.[index]?.value
                                                            ? criteria?.[index]?.value.split(',')
                                                            : []
                                                    } // Değerleri array olarak al
                                                    onChange={(event: any) => {
                                                        const selectedValues = event.target.value; // Çoklu seçim için array döner
                                                        const formattedValue = selectedValues.join(','); // Seçilenleri virgülle ayır
                                                        updateChildItem(
                                                            formattedValue,
                                                            item.key,
                                                            operators?.[item.operator]?.value,
                                                            index
                                                        );
                                                    }}
                                                    renderValue={(selected: any) =>
                                                        formatSelectedCriteriaNames(item.criteriaTypeValues || [], selected)
                                                    }
                                                    label={t('ruleManagement.fields.rule.values.value')}
                                                >
                                                    {(item.criteriaTypeValues || []).map((criteriaTypeValue: any) => {
                                                        const selectedValues =
                                                            criteria?.[index]?.value?.split(',') || [];
                                                        const isSelected = selectedValues.includes(
                                                            criteriaTypeValue.value
                                                        );

                                                        return (
                                                            <MenuItem
                                                                className={RTM.predefinedSelectItem}
                                                                key={criteriaTypeValue.id}
                                                                value={criteriaTypeValue.value}
                                                                data-criteria-type-value={criteriaTypeValue.value}
                                                                onClick={() => {
                                                                    const newSelectedValues =
                                                                        toggleCriteriaSelectedValue(
                                                                            selectedValues,
                                                                            criteriaTypeValue.value,
                                                                            isSelected
                                                                        );

                                                                    updateChildItem(
                                                                        newSelectedValues.join(','),
                                                                        item.key,
                                                                        operators?.[item.operator]?.value,
                                                                        index
                                                                    );
                                                                }}
                                                            >
                                                                <Checkbox className={RTM.predefinedCheckbox} checked={isSelected} />
                                                                <ListItemText className={RTM.predefinedItemText} primary={criteriaTypeValue.name} />
                                                            </MenuItem>
                                                        );
                                                    })}
                                                </Select>
                                                <Box
                                                    className={RTM.selectAllWrap}
                                                    sx={{
                                                        backgroundColor: '#f5f5f5',
                                                        marginLeft: '8px',
                                                        height: '4rem',
                                                        display: 'flex',
                                                        borderRadius: '8px',
                                                    }}
                                                >
                                                    <FormControlLabel
                                                        sx={{ padding: '10px' }}
                                                        control={
                                                            <Switch
                                                                className={RTM.selectAllToggle}
                                                                sx={{ flex: 6 }}
                                                                title={'Hepsi'}
                                                                id={'all'}
                                                                checked={selectedAllIndex?.[index]?.disabled}
                                                                onChange={() => {
                                                                    if (selectedAllIndex?.[index]?.disabled) {
                                                                        setSelectedAll(
                                                                            !selectedAllIndex?.[index]?.disabled,
                                                                            index
                                                                        );
                                                                        updateChildItem(
                                                                            '',
                                                                            item.key,
                                                                            operators?.[item.operator]?.value,
                                                                            index
                                                                        );
                                                                    } else {
                                                                        setSelectedAll(
                                                                            !selectedAllIndex?.[index]?.disabled,
                                                                            index
                                                                        );
                                                                        updateChildItem(
                                                                            item.criteriaTypeValues
                                                                                ?.map((value: any) => value.value)
                                                                                .join(','),
                                                                            item.key,
                                                                            operators?.[item.operator]?.value,
                                                                            index
                                                                        );
                                                                    }
                                                                }}
                                                            />
                                                        }
                                                        label="Hepsi"
                                                    />
                                                </Box>
                                            </FormControl>
                                        )}
                                    </Box>
                                </Box>
                            ))
                        ) : (
                            <Box
                                className={RTM.emptyCriteria}
                                sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                            >
                                <Typography variant="subtitle1" gutterBottom>
                                    {t('ruleManagement.fields.rule.emptyCriteria')}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Grid>
            </form>

            <Dialog className={RTM.jsonDialog} open={jsonDialogOpen} onClose={handleJsonDialogClose}>
                <DialogContent className={RTM.jsonDialogContent}>
                    <TextField
                        className={RTM.jsonInput}
                        autoFocus
                        margin="dense"
                        id="jsonInput"
                        label={t('ruleManagement.jsonInputLabel')}
                        type="text"
                        fullWidth
                        multiline
                        rows={4}
                        value={jsonInput}
                        onChange={(e) => setJsonInput(e.target.value)}
                    />
                </DialogContent>
                <DialogActions className={RTM.jsonDialogActions}>
                    <Button className={RTM.jsonCancelButton} onClick={handleJsonDialogClose} color="primary">
                        {t('general.cancel')}
                    </Button>
                    <Button className={RTM.jsonAcceptButton} onClick={handleJsonSubmit} color="primary">
                        {t('general.accept')}
                    </Button>
                </DialogActions>
            </Dialog>
        </SCUIGeneralModal>
    );
};
